import PocketBase from 'pocketbase';

class BeszelAPI {
  constructor() {
    this.pb = null;
    this.baseUrl = '';
    this.isAuthenticated = false;
  }

  async initialize(baseUrl, email, password) {
    try {
      this.baseUrl = baseUrl.replace(/\/$/, ''); // 移除末尾的斜杠
      this.pb = new PocketBase(this.baseUrl);
      
      console.log('=== Beszel API 初始化开始 ===');
      console.log('服务器地址:', this.baseUrl);
      console.log('用户邮箱:', email);
      
      // 检查服务器连接
      try {
        console.log('检查服务器连接...');
        const response = await fetch(this.baseUrl);
        console.log('服务器响应状态:', response.status);
      } catch (fetchError) {
        console.error('服务器连接检查失败:', fetchError);
        throw new Error(`无法连接到服务器: ${fetchError.message}`);
      }
      
      // 使用Beszel的认证方式
      try {
        console.log('尝试用户认证...');
        const authData = await this.pb.collection('users').authWithPassword(email, password);
        console.log('认证成功:', authData);
        
        // 验证认证状态
        console.log('认证状态验证:');
        console.log('- isValid:', this.pb.authStore.isValid);
        console.log('- token:', this.pb.authStore.token);
        console.log('- user ID:', this.pb.authStore.record?.id);
        
        this.isAuthenticated = this.pb.authStore.isValid;
        
        if (!this.isAuthenticated) {
          throw new Error('认证失败：token无效');
        }
        
      } catch (authError) {
        console.error('认证失败:', authError);
        throw new Error(`认证失败: ${authError.message}`);
      }
      
      // 测试连接并获取可用的集合
      await this.testConnection();
      
      console.log('=== Beszel API 初始化完成 ===');
      return { success: true };
    } catch (error) {
      console.error('Beszel API 初始化失败:', error);
      return { success: false, error: error.message };
    }
  }

  async testConnection() {
    console.log('=== 测试连接开始 ===');
    
    // 尝试获取集合列表
    try {
      console.log('尝试获取集合列表...');
      const collections = await this.pb.send('/api/collections', {});
      console.log('可用集合:', collections);
    } catch (error) {
      console.log('获取集合列表失败:', error.message);
    }
    
    // 测试一些可能的集合
    const possibleCollections = [
      'systems', 'system', 'servers', 'hosts', 'nodes', 
      'stats', 'statistics', 'metrics', 'data'
    ];
    
    for (const collectionName of possibleCollections) {
      try {
        console.log(`测试集合: ${collectionName}`);
        const result = await this.pb.collection(collectionName).getList(1, 1);
        console.log(`集合 ${collectionName} 可用，示例数据:`, result);
      } catch (error) {
        console.log(`集合 ${collectionName} 不可用:`, error.message);
      }
    }
    
    console.log('=== 测试连接完成 ===');
  }

  async getSystems() {
    if (!this.isAuthenticated) {
      throw new Error('未认证');
    }

    const possibleCollections = ['systems', 'system', 'servers', 'hosts', 'nodes'];
    
    for (const collectionName of possibleCollections) {
      try {
        console.log(`尝试获取系统数据从集合: ${collectionName}`);
        const records = await this.pb.collection(collectionName).getFullList({
          sort: '-created',
        });
        console.log(`成功获取 ${collectionName} 数据:`, records);
        return records;
      } catch (error) {
        console.log(`集合 ${collectionName} 失败:`, error.message);
        continue;
      }
    }
    
    throw new Error('无法找到系统数据集合');
  }

  async getSystemStats(systemId, timeRange = '1h') {
    if (!this.isAuthenticated) {
      throw new Error('未认证');
    }

    const possibleCollections = ['stats', 'statistics', 'metrics', 'data'];
    
    for (const collectionName of possibleCollections) {
      try {
        console.log(`尝试统计集合: ${collectionName}`);
        const now = new Date();
        const timeAgo = new Date(now.getTime() - this.getTimeRangeMs(timeRange));
        
        const records = await this.pb.collection(collectionName).getFullList({
          filter: `system = "${systemId}" && created >= "${timeAgo.toISOString()}"`,
          sort: '-created',
        });
        
        console.log(`成功获取 ${collectionName} 统计数据:`, records);
        return records;
      } catch (error) {
        console.log(`统计集合 ${collectionName} 失败:`, error.message);
        continue;
      }
    }
    
    throw new Error('无法找到统计数据集合');
  }

  async getSystemsWithLatestStats() {
    if (!this.isAuthenticated) {
      throw new Error('未认证');
    }

    try {
      console.log('获取系统列表...');
      const systems = await this.getSystems();
      
      if (systems.length === 0) {
        console.log('没有找到系统数据');
        return [];
      }
      
      console.log('找到系统:', systems);
      
      // 尝试获取统计数据
      const possibleStatsCollections = ['stats', 'statistics', 'metrics', 'data'];
      let statsCollection = null;
      
      for (const collectionName of possibleStatsCollections) {
        try {
          console.log(`测试统计集合: ${collectionName}`);
          await this.pb.collection(collectionName).getList(1, 1);
          statsCollection = collectionName;
          console.log(`使用统计集合: ${statsCollection}`);
          break;
        } catch (error) {
          console.log(`统计集合 ${collectionName} 不可用:`, error.message);
          continue;
        }
      }
      
      if (!statsCollection) {
        console.log('未找到统计数据集合，返回无统计数据的系统');
        return systems.map(system => ({
          ...system,
          latestStats: null,
        }));
      }
      
      // 为每个系统获取最新统计数据
      const systemsWithStats = await Promise.all(
        systems.map(async (system) => {
          try {
            console.log(`获取系统 ${system.id} 的统计数据...`);
            const stats = await this.pb.collection(statsCollection).getList(1, 1, {
              filter: `system = "${system.id}"`,
              sort: '-created',
            });
            
            return {
              ...system,
              latestStats: stats.items[0] || null,
            };
          } catch (error) {
            console.log(`获取系统 ${system.id} 统计失败:`, error.message);
            return {
              ...system,
              latestStats: null,
            };
          }
        })
      );
      
      console.log('最终系统数据:', systemsWithStats);
      return systemsWithStats;
    } catch (error) {
      console.error('获取系统和统计数据失败:', error);
      throw error;
    }
  }

  getTimeRangeMs(timeRange) {
    const timeRanges = {
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
    };
    
    return timeRanges[timeRange] || timeRanges['1h'];
  }

  disconnect() {
    if (this.pb) {
      this.pb.authStore.clear();
      this.isAuthenticated = false;
    }
  }
}

export default new BeszelAPI();